import React, { useRef, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Image } from '@sainsburys-tech/bolt'
import { canUseDom } from '@sainsburys-tech/boltui-utils'
import throttle from 'lodash.throttle'
import * as Styled from './M052.styles'
import NavChevron from './NavChevron/NavChevron.jsx'
import { toggleChevronVisibility, getLinkLeftPositions, getNextViewLeft } from './helpers/M052.helper.js'
import { createGolDataAttributes, joinLinkTitles } from '../../../helpers/tracking'
import { useWindowDimensions } from '../../../helpers/useWindowDimensions'

/**
 * - M052 is an image and text navigation or brand navigation that is predominantly for acting as a filter on PLP pages.
 * - Can be either used with images and text as links or text only pills as links.
 * - Can be used to display brands in a slider or stacked, with or without a border and in lanscape or square ratio
 */
const M052 = ({
  alignment,
  appliedFilters,
  heading,
  linkTarget,
  links,
  navigation,
  isBrands,
  layout,
  imageStyle,
  hasBorder
}) => {
  const mobileLayoutBreakpoint = '575.98'
  const { screenWidth } = useWindowDimensions()
  const sliderLayout = layout === 'slider' || screenWidth <= mobileLayoutBreakpoint
  const hideChevron = true
  const navRef = useRef(null)
  const navParams = useRef({
    offsetWidth: null,
    scrollWidth: null,
    linkLeftPositions: null
  })

  const [chevronVisibility, setChevronVisibility] = useState({
    left: hideChevron,
    right: hideChevron
  })

  const updateChevrons = () => {
    const scrollLeft = Math.ceil(navRef.current.scrollLeft)
    const scrollRight = navParams.current.scrollWidth - (scrollLeft + navParams.current.offsetWidth)
    setChevronVisibility({
      left: toggleChevronVisibility(navParams, scrollLeft),
      right: toggleChevronVisibility(navParams, scrollRight)
    })
  }

  const handleScroll = useCallback(
    throttle(() => {
      updateChevrons()
    }, 150),
    []
  )

  const updateNavParams = () => {
    const { offsetWidth, scrollWidth } = navRef.current
    const linkLeftPositions = getLinkLeftPositions(navRef)

    navParams.current = {
      offsetWidth,
      scrollWidth,
      linkLeftPositions
    }
  }

  const updateNavParamsAndChevrons = () => {
    if (sliderLayout) {
      updateNavParams()
      updateChevrons()
    }
  }

  useEffect(() => {
    window.addEventListener('resize', throttle(updateNavParamsAndChevrons), 400)
    updateNavParamsAndChevrons()
    return () => window.removeEventListener('resize', updateNavParamsAndChevrons)
  }, [screenWidth])

  const isPillsView = !links[0].backgroundImageUrl

  const scrollToSlide = (nextSlideLeft) => {
    const scrollParams = { left: nextSlideLeft, behavior: 'smooth' }
    if (navRef.current.scrollTo) navRef.current.scrollTo(scrollParams)
  }

  const goToView = (direction) => {
    const nextSlideLeft = getNextViewLeft(navRef, navParams, direction, isBrands)
    scrollToSlide(nextSlideLeft)
  }

  const getGOLDataAttributes = (linkTitle, url, action) => {
    const linkTitlesArray = navigation ? [navigation.linkTitle] : []
    return createGolDataAttributes(
      'isolated',
      'M052 - NA',
      action === 'Load' ? joinLinkTitles(links, linkTitlesArray) : linkTitle,
      url,
      heading,
      action
    )
  }

  const addGolTracking = (evt, linkTitle, url) => {
    const dataAttributes = getGOLDataAttributes(linkTitle, url)
    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const Item = isBrands ? Styled.Brand : Styled.Link
  const ItemsWrapper = sliderLayout ? Styled.SliderLinksWrapper : Styled.StackedBrandsWrapper

  const navTitle = () => {
    const { linkUrl, linkTitle, linkName } = navigation
    return (
      <Styled.TitleWrapper>
        <a
          data-test='m052-linkTitle'
          href={linkUrl}
          title={linkTitle}
          target={linkTarget}
          onClick={(evt) => addGolTracking(evt, linkTitle, linkUrl)}
        >
          {linkName}
        </a>
      </Styled.TitleWrapper>
    )
  }

  const navHeading = () => {
    return (
      <Styled.TitleWrapper>
        <Styled.Heading data-test='m052-heading'>{heading}</Styled.Heading>
      </Styled.TitleWrapper>
    )
  }

  const navLinks = () => {
    const allLinks = []
    links.map((link, index) => {
      const { linkName, linkTitle, backgroundImageUrl, linkUrl, image = {} } = link
      const filterValue = linkTitle && linkTitle.toLowerCase()
      let isActive = false

      if (isPillsView) {
        appliedFilters.some((filter) => {
          if (filter.values.length === 1 && filter.values.indexOf(filterValue) !== -1) {
            isActive = true
            return true
          }
          return false
        })

        return allLinks.push(
          <Styled.M052Pill
            data-test={`m052-pill-${index}`}
            href={linkUrl}
            target={linkTarget}
            label={linkName}
            key={`pill-link-${index}`}
            active={isActive}
            onClick={(evt) => addGolTracking(evt, linkTitle, linkUrl)}
          />
        )
      }

      const baseUrl = canUseDom() ? linkUrl.replace(window.location.origin, '').split('?')[0] : null

      if (canUseDom() && window.location.href.indexOf(baseUrl) !== -1) {
        isActive = true
      }

      const imageURL = image.source || backgroundImageUrl
      const imageAltText = image.altText || linkTitle

      return allLinks.push(
        <Item
          {...getGOLDataAttributes(linkTitle, linkUrl, 'Load')}
          data-test={`m052-${isBrands ? 'brand' : 'navItem'}-${index}`}
          isActive={isActive}
          forwardedAs='a'
          href={linkUrl}
          target={linkTarget}
          title={linkTitle}
          key={`nav-link-${index}`}
          onClick={(evt) => addGolTracking(evt, linkName || linkTitle, linkUrl)}
          isBrands={isBrands}
          layout={layout}
          hasBorder={hasBorder}
          gutterSize='16px'
          gutterValue={1}
          lg={imageStyle === 'square' ? 2.4 : 3}
          md={imageStyle === 'square' ? 3 : 4}
          xs={6}
          numberOfColumns={12}
          imageStyle={imageStyle}
        >
          <Image
            width={isBrands ? undefined : 80}
            height={isBrands ? undefined : 80}
            alt={imageAltText}
            src={imageURL}
            data-test={`image-${index}`}
          />
          {linkName}
        </Item>
      )
    })
    return (
      <ItemsWrapper
        ref={navRef}
        onScroll={handleScroll}
        chevronVisibilityLeft={!chevronVisibility.left}
        chevronVisibilityRight={!chevronVisibility.right}
        alignment={alignment}
        isBrands={isBrands}
        isPills={isPillsView}
        layout={layout}
        data-test='m052-links'
        gutterValue={1}
        row='xs'
      >
        {allLinks}
      </ItemsWrapper>
    )
  }

  return (
    <Styled.Container
      isPillsView={isPillsView}
      data-test='m052'
      isBrands={isBrands}
      {...getGOLDataAttributes('', navigation ? navigation?.linkUrl : links[0].linkUrl, 'Load')}
    >
      {heading && navHeading()}
      {navigation && navTitle()}
      <Styled.NavWrapper data-test='m052-nav-parent'>
        <NavChevron
          dir='left'
          hide={chevronVisibility.left}
          goToView={goToView}
          imageStyle={isPillsView ? undefined : imageStyle}
        />
        {navLinks()}
        <NavChevron
          dir='right'
          hide={chevronVisibility.right}
          goToView={goToView}
          imageStyle={isPillsView ? undefined : imageStyle}
        />
      </Styled.NavWrapper>
    </Styled.Container>
  )
}

M052.propTypes = {
  /** Optional text for heading */
  heading: PropTypes.string,
  /** Required array of links to be displayed, min 3 */
  links: PropTypes.arrayOf(
    PropTypes.shape({
      /** Link label */
      linkName: PropTypes.string,
      /** Optional link title, can be seen when hovering over the image */
      linkTitle: PropTypes.string,
      /** Optional background image url */
      backgroundImageUrl: PropTypes.string,
      /** Link URL */
      linkUrl: PropTypes.string,
      /** Optional image */
      image: PropTypes.shape({
        /** Image URL */
        source: PropTypes.string.isRequired,
        /** Alt text for the image */
        altText: PropTypes.string.isRequired
      })
    })
  ).isRequired,
  /** Option of opening in a new tab, defaults to an empty string  */
  linkTarget: PropTypes.oneOf(['', '_blank', '_self']),
  navigation: PropTypes.shape({
    /** Link label */
    linkName: PropTypes.string,
    /** Optional link title, can be seen when hovering over the pill */
    linkTitle: PropTypes.string,
    /** Link URL */
    linkUrl: PropTypes.string
  }),
  /** Array of selected filters */
  appliedFilters: PropTypes.array,
  /** When prop is center the links are center aligned */
  alignment: PropTypes.string,
  /** Check if it's brands variant, defaults to false */
  isBrands: PropTypes.bool,
  /** Layout of the items, defaults to 'slider' */
  layout: PropTypes.oneOf(['slider', 'stack']),
  /** Optional style for image; defaults to 'square' */
  imageStyle: PropTypes.oneOf(['square', 'landscape']),
  /** Optional border for each item, defaults to false */
  hasBorder: PropTypes.bool
}

M052.defaultProps = {
  alignment: 'left',
  appliedFilters: [],
  heading: '',
  linkTarget: '',
  isBrands: false,
  layout: 'slider',
  imageStyle: 'landscape',
  hasBorder: false
}

export default M052
